import {DOMAttrs} from '@codeurs/front'
import {ErrorMessage, Field, useField} from 'formik'
import Pikaday from 'pikaday'
import React, {FunctionComponent, RefObject, useEffect, useRef} from 'react'
import AnimateHeight from 'react-animate-height'
import {
	CalendarGreyIcon,
	CalendarIcon,
	ClockIcon,
	ErrorIcon
} from 'styles/inline-icons'
import {style} from 'util/campagne/style'
import {fromDisplayDate, toDisplayDate} from 'util/dates'
import {useTranslation} from 'util/i18n'
import {scrollTo} from 'util/scrollmemory'
import './datepicker.scss'
import {
	fieldStyles,
	FormfieldCheckbox,
	FormFieldHourSelector
} from './formfield'

const styles = style('datepicker', ['multiple'])

export const DatePicker: FunctionComponent<DOMAttrs> = ({
	label,
	error,
	name,
	placeholder,
	onChange = null,
	hideIcon = false,
	toFormat = 'dd/MM/yyyy',
	...attrs
}) => {
	const dateRef: RefObject<HTMLInputElement> = useRef()
	const [, meta, helpers] = useField(name)
	const showError = !!meta.error
	useEffect(() => {
		const picker = new Pikaday({
			field: dateRef.current,
			onSelect: (date: Date) => helpers.setValue(toDisplayDate(date, toFormat)),
			format: toFormat,
			toString: toDisplayDate,
			parse: fromDisplayDate,
			...attrs
		})
		return () => picker.destroy()
	}, [])
	const element = useRef<HTMLDivElement>()
	useEffect(() => {
		if (showError) scrollTo(() => 0, element.current)
	}, [showError])
	return (
		<div class={fieldStyles} ref={element}>
			<div class={fieldStyles('container')} ref={dateRef}>
				{label && <div class={fieldStyles('label').mod('row')}>{label}</div>}
				<div
					class={fieldStyles('input')
						.mod('datepicker')
						.mod(label && 'label')
						.is({error: showError})}
				>
					<Field
						class={fieldStyles('input-value').mod({hideicon: hideIcon})}
						name={name}
						id={name}
						placeholder={placeholder}
						{...attrs}
					/>
				</div>
				<ErrorIcon
					className={fieldStyles('error-icon')
						.is({visible: showError})
						.toString()}
				/>
				{!error && !hideIcon && (
					<div className={fieldStyles('icon')}>{<CalendarIcon />}</div>
				)}
			</div>
			<AnimateHeight height={showError ? 'auto' : 0}>
				<div class={fieldStyles('error')}>
					<ErrorMessage name={name} />
				</div>
			</AnimateHeight>
		</div>
	)
}

export const DatePickerMultiple: FunctionComponent<
	DOMAttrs & {
		multipleDays?: string
		endHour?: string
		date_start_name?: string
		date_end_name?: string
		hour_start_name?: string
		hour_end_name?: string
	}
> = ({
	date_start_name = 'date_start',
	date_end_name = 'date_end',
	hour_start_name = 'hour_start',
	hour_end_name = 'hour_end',
	multipleDays = 'date_has_multiple_days',
	endHour = 'date_has_end_hour',
	values
}) => {
	const t = useTranslation('campagne').register

	const {date_has_multiple_days, date_has_end_hour} = values
	return (
		<div class={styles.multiple}>
			<label class={styles.multiple('title')}>{t.when}</label>
			<div class={styles.multiple('col')}>
				<div class={styles.multiple('row')}>
					<label class={styles.multiple('row-container')}>
						<CalendarGreyIcon />
						<div class={styles.multiple('row-container-datepicker')}>
							<DatePicker
								name={date_start_name}
								hideIcon
								placeholder="dd/mm/yyyy"
							/>
						</div>
					</label>
					<div
						class={styles
							.multiple('row-container')
							.is({disabled: !date_has_multiple_days})}
					>
						<CalendarGreyIcon />
						<div class={styles.multiple('row-container-datepicker')}>
							<DatePicker
								name={date_end_name}
								hideIcon
								tabIndex={!date_has_multiple_days && -1}
								placeholder="dd/mm/yyyy"
							/>
						</div>
					</div>
				</div>
				<div class={styles.multiple('row').mod('checkbox')}>
					<FormfieldCheckbox small name={multipleDays}>
						{t.multiple_days}
					</FormfieldCheckbox>
				</div>
			</div>
			<div class={styles.multiple('col')}>
				<div class={styles.multiple('row')}>
					<label class={styles.multiple('row-container')}>
						<ClockIcon />
						<div
							class={styles.multiple('row-container-datepicker').mod('hour')}
						>
							<div class={styles.multiple('row-container-datepicker-label')}>
								<span>{t.at}</span>
								<FormFieldHourSelector name={hour_start_name} />
								<span>{t.h}</span>
							</div>
						</div>
					</label>
					<div
						class={styles
							.multiple('row-container')
							.is({disabled: !date_has_end_hour})}
					>
						<ClockIcon />
						<div
							class={styles.multiple('row-container-datepicker').mod('hour')}
						>
							<label class={styles.multiple('row-container-datepicker-label')}>
								<span>{t.until}</span>
								<FormFieldHourSelector
									name={hour_end_name}
									tabIndex={!date_has_end_hour && -1}
								/>
								<span>{t.h}</span>
							</label>
						</div>
					</div>
				</div>
				<div class={styles.multiple('row').mod('checkbox')}>
					<FormfieldCheckbox small name={endHour}>
						{t.end_hour}
					</FormfieldCheckbox>
				</div>
			</div>
		</div>
	)
}
